import React from "react";

import { IgChip, IgTooltip, IgTypography } from "@intelligems/ig-theme";
import { officialStatusName } from "@intelligems/ig-utils";
import { Grid, Skeleton, useTheme } from "@mui/material";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useGetExperienceSchedules } from "@src/api/schedules/get-schedules-by-experience-id";
import { ExperienceDurationTooltip } from "@src/features/experiments/components/ExperienceDurationTooltip";
import { Experience } from "@src/store/slices/experiences/types";

interface StatusRendererProps {
  experience: Experience;
  renderLocation: "experience" | "overview";
}

export const StatusRenderer = ({
  experience,
  renderLocation,
}: StatusRendererProps) => {
  const schedule = useGetExperienceSchedules(experience.id);
  const organization = useGetOrg();
  const theme = useTheme();
  const SkeletonColors = {
    pending: theme.palette.text.interactive.neutral.disabled, // exception to the icon fill rules, used in experiment table only
    started: theme.igIconColors.success,
    paused: theme.igIconColors.warning,
    ended: theme.igIconColors.error,
  };

  if (schedule.isSuccess && schedule.data.schedules.length) {
    return (
      <IgTooltip
        title={
          <ExperienceDurationTooltip
            experience={experience}
            organization={organization.data.org}
            schedules={schedule.data.schedules}
          />
        }
      >
        <IgChip
          size={"small"}
          sx={{
            backgroundColor: `${renderLocation === "experience" ? "white" : "transparent"} !important`,
          }}
          variant={
            "viewMode" in experience && experience.viewMode !== undefined
              ? "outlined"
              : undefined
          }
          label={
            <Grid
              container
              columnSpacing={1}
              alignItems={"center"}
              flexWrap={"nowrap"}
            >
              <Grid item>
                <Skeleton
                  animation={false}
                  variant={"circular"}
                  sx={{
                    width: "6px",
                    height: "6px",
                    backgroundColor: SkeletonColors[experience.status],
                  }}
                />
              </Grid>
              <Grid item>
                <IgTypography color={theme.palette.text.secondary}>
                  {experience.status === "pending"
                    ? "Scheduled"
                    : `${officialStatusName(experience.status)} in Schedule`}
                </IgTypography>
              </Grid>
            </Grid>
          }
        />
      </IgTooltip>
    );
  }

  return (
    <IgTooltip
      title={
        <ExperienceDurationTooltip
          experience={experience}
          organization={organization.data.org}
          schedules={schedule?.data?.schedules ?? []}
        />
      }
    >
      <IgChip
        size={"small"}
        sx={{
          backgroundColor: `${renderLocation === "experience" ? "white" : "transparent"} !important`,
          ...(renderLocation === "overview" && {
            border: "none",
          }),
        }}
        variant={
          "viewMode" in experience && experience.viewMode !== undefined
            ? "outlined"
            : undefined
        }
        // status={color}
        label={
          <Grid
            container
            columnSpacing={1}
            alignItems={"center"}
            flexWrap={"nowrap"}
          >
            <Grid item>
              <Skeleton
                animation={false}
                variant={"circular"}
                sx={{
                  width: "6px",
                  height: "6px",
                  backgroundColor: SkeletonColors[experience.status],
                }}
              />
            </Grid>
            <Grid item>
              <IgTypography color={theme.palette.text.secondary}>
                {officialStatusName(experience.status)}
              </IgTypography>
            </Grid>
          </Grid>
        }
      />
    </IgTooltip>
  );
};
